import {
  type KeyboardEvent,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { connectorDirectory } from "../lib/connectorDirectory";
import {
  activeTriggerToken,
  slashCompletionSuffix,
  type SlashToken,
} from "../lib/slashToken";

export type MentionStep = { name: string; color: string };
export type MentionConnector = { name: string; identifier: string };
export type MentionConnectorCatalogEntry = {
  name: string;
  identifier: string;
  keywords?: string;
  popular?: boolean;
};

export const mentionConnectorCatalog: readonly MentionConnectorCatalogEntry[] =
  connectorDirectory.map((c) => ({
    name: c.name,
    identifier: c.identifier,
    keywords: c.definition.keywords,
    popular: c.tags.includes("featured"),
  }));

export type MentionItem =
  | { kind: "step"; id: string; name: string; color: string; section: string }
  | {
      kind: "connector";
      id: string;
      name: string;
      identifier: string;
      section: string;
    };

export type MentionCompletionState = {
  token: SlashToken | null;
  matches: readonly MentionItem[];
  selectedIndex: number;
  caret: number;
  suffix: string;
  catalogTotal: number;
  onSelect: (item: MentionItem) => void;
};

const STEPS_SECTION = "Steps";
const AVAILABLE_SECTION = "Available connectors";
const ALL_SECTION = "All connectors";
const POPULAR_SAMPLE_COUNT = 8;
const CATALOG_SEARCH_LIMIT = 25;

type UseMentionsParams = {
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  setValue: (v: string) => void;
  steps: readonly MentionStep[];
  connectors: readonly MentionConnector[];
  tenantConnectors?: readonly MentionConnector[];
  connectorCatalog?: readonly MentionConnectorCatalogEntry[];
  contextLabel?: string;
  onAfterAccept?: () => void;
  enabled?: boolean;
};

export function useMentions({
  textareaRef,
  value,
  setValue,
  steps,
  connectors,
  tenantConnectors,
  connectorCatalog,
  contextLabel = "On this workflow",
  onAfterAccept,
  enabled = true,
}: UseMentionsParams) {
  const [token, setToken] = useState<SlashToken | null>(null);

  const stepItems = useMemo<MentionItem[]>(
    () =>
      enabled
        ? steps.map((s) => ({
            kind: "step" as const,
            id: `step:${s.name}`,
            name: s.name,
            color: s.color,
            section: STEPS_SECTION,
          }))
        : [],
    [enabled, steps]
  );

  const [selectedSteps, setSelectedSteps] = useState<MentionStep[]>([]);
  const addStep = useCallback((step: MentionStep) => {
    setSelectedSteps((prev) =>
      prev.some((s) => s.name === step.name) ? prev : [...prev, step]
    );
  }, []);
  const removeStep = useCallback((name: string) => {
    setSelectedSteps((prev) => prev.filter((s) => s.name !== name));
  }, []);
  const clearSteps = useCallback(() => setSelectedSteps([]), []);

  const [selectedConnectors, setSelectedConnectors] = useState<
    MentionConnector[]
  >([]);
  const addConnector = useCallback((connector: MentionConnector) => {
    setSelectedConnectors((prev) =>
      prev.some((c) => c.name === connector.name) ? prev : [...prev, connector]
    );
  }, []);
  const removeConnector = useCallback((name: string) => {
    setSelectedConnectors((prev) => prev.filter((c) => c.name !== name));
  }, []);
  const clearConnectors = useCallback(() => setSelectedConnectors([]), []);

  const syncMentionToken = useCallback(
    (el: HTMLTextAreaElement) => {
      if (!enabled) return;
      const next =
        el.value === ""
          ? null
          : activeTriggerToken(el.value, el.selectionStart ?? 0, "@");
      setToken((prev) =>
        prev &&
        next &&
        prev.start === next.start &&
        prev.end === next.end &&
        prev.query === next.query
          ? prev
          : next
      );
    },
    [enabled]
  );

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!enabled || !el) return;
    syncMentionToken(el);
  }, [enabled, syncMentionToken, textareaRef]);

  const [popularOrder, setPopularOrder] = useState<readonly MentionConnector[]>(
    []
  );
  const menuOpenRef = useRef(false);
  const menuOpen = token !== null;
  // Draw a fresh popular shuffle once per "@" open — held in state so it stays
  // put across re-renders instead of reshuffling on every one.
  useLayoutEffect(() => {
    if (menuOpen && !menuOpenRef.current) {
      const popular = (connectorCatalog ?? [])
        .filter((c) => c.popular)
        .map((c) => ({ name: c.name, identifier: c.identifier }));
      // Fisher–Yates: an even sample, unlike sort(() => Math.random() - 0.5).
      for (let i = popular.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [popular[i], popular[j]] = [popular[j]!, popular[i]!];
      }
      setPopularOrder(popular);
    }
    menuOpenRef.current = menuOpen;
  }, [menuOpen, connectorCatalog]);

  const matches = useMemo<MentionItem[]>(() => {
    if (!token) return [];
    const q = token.query.toLowerCase();
    const nameMatches = (name: string) => name.toLowerCase().includes(q);
    const toItem = (c: MentionConnector, section: string): MentionItem => ({
      kind: "connector",
      id: `connector:${c.name}`,
      name: c.name,
      identifier: c.identifier,
      section,
    });

    const stepMatches = stepItems.filter((i) => nameMatches(i.name));

    const contextMatches = connectors
      .filter((c) => nameMatches(c.name))
      .map((c) => toItem(c, contextLabel));

    const contextNames = new Set(connectors.map((c) => c.name));
    const tenantMatches = (tenantConnectors ?? [])
      .filter((c) => !contextNames.has(c.name) && nameMatches(c.name))
      .map((c) => toItem(c, AVAILABLE_SECTION));

    const shownNames = new Set([
      ...connectors.map((c) => c.name),
      ...(tenantConnectors ?? []).map((c) => c.name),
    ]);
    const catalog = connectorCatalog ?? [];
    // Browsing shows the popular sample (drawn once per open); typing searches
    // the whole catalog by name and keywords. Either way skip ones listed above.
    const catalogMatches =
      q === ""
        ? popularOrder
            .filter((c) => !shownNames.has(c.name))
            .slice(0, POPULAR_SAMPLE_COUNT)
            .map((c) => toItem(c, ALL_SECTION))
        : catalog
            .filter(
              (c) =>
                !shownNames.has(c.name) &&
                (nameMatches(c.name) ||
                  (c.keywords ?? "").toLowerCase().includes(q))
            )
            .slice(0, CATALOG_SEARCH_LIMIT)
            .map((c) => toItem(c, ALL_SECTION));

    return [
      ...stepMatches,
      ...contextMatches,
      ...tenantMatches,
      ...catalogMatches,
    ];
  }, [
    token,
    stepItems,
    connectors,
    tenantConnectors,
    connectorCatalog,
    contextLabel,
    popularOrder,
  ]);

  const [selected, setSelected] = useState(0);
  useEffect(() => {
    // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
    setSelected(0);
  }, [token?.start, token?.query]);
  const selectedIndex =
    matches.length > 0 ? Math.min(selected, matches.length - 1) : 0;

  const caret = token ? token.start + 1 + token.query.length : 0;
  const suffix = slashCompletionSuffix(
    token,
    caret,
    matches.length > 0 ? matches[selectedIndex]!.name : null
  );

  const acceptMention = useCallback(
    (item: MentionItem) => {
      if (!token) return;
      const before = value.slice(0, token.start);
      const after = value.slice(token.end);
      // Add a trailing space only at the end of the input; mid-message there's already whitespace after the token.
      const inserted = `@${item.name}${after === "" ? " " : ""}`;
      const caret = before.length + inserted.length;
      setValue(before + inserted + after);
      if (item.kind === "step") addStep({ name: item.name, color: item.color });
      else addConnector({ name: item.name, identifier: item.identifier });
      setToken(null);
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (el) {
          el.focus();
          el.setSelectionRange(caret, caret);
        }
        onAfterAccept?.();
      });
    },
    [token, value, addStep, addConnector, textareaRef, setValue, onAfterAccept]
  );

  const handleMentionKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>): boolean => {
      if (matches.length === 0) return false;
      const atEnd =
        e.currentTarget.selectionStart === e.currentTarget.selectionEnd &&
        e.currentTarget.selectionStart === token?.end;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelected((i) => Math.min(i + 1, matches.length - 1));
        return true;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelected((i) => Math.max(i - 1, 0));
        return true;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        acceptMention(matches[selectedIndex]!);
        return true;
      }
      if (e.key === "ArrowRight" && atEnd && suffix !== "") {
        e.preventDefault();
        acceptMention(matches[selectedIndex]!);
        return true;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setToken(null);
        return true;
      }
      return false;
    },
    [matches, selectedIndex, acceptMention, token, suffix]
  );

  const catalogTotal = connectorCatalog?.length ?? 0;

  return {
    selectedSteps,
    addStep,
    removeStep,
    clearSteps,
    selectedConnectors,
    addConnector,
    removeConnector,
    clearConnectors,
    syncMentionToken,
    mentionCompletion: {
      token,
      matches,
      selectedIndex,
      caret,
      suffix,
      catalogTotal,
      onSelect: acceptMention,
    } satisfies MentionCompletionState,
    handleMentionKeyDown,
  };
}
